import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { maxBy, minBy, sumBy } from "lodash";
import { useFragment } from "react-relay";

import { HStack, Text } from "@whoosh/design-system/src/components/primitive";

import { useFacilityShowLessonPrices } from "../../../features/Facility/hooks/useFacilityShowLessonPrices";
import { PriceDisplay } from "../components/PriceDisplay";
import { lessonTypeHasRateForMembershipType } from "../helpers/lessonTypeHasRateForMembershipType";
import { useGetLessonTypeOptions_facility$key } from "./__generated__/useGetLessonTypeOptions_facility.graphql";
import { useGetLessonTypeOptions_session$key } from "./__generated__/useGetLessonTypeOptions_session.graphql";
import {
  useGetLessonTypeOptions_staffProfile$data,
  useGetLessonTypeOptions_staffProfile$key,
} from "./__generated__/useGetLessonTypeOptions_staffProfile.graphql";

type LessonTypeOption = {
  value: string;
  label: React.JSX.Element;
  name?: string;
  duration?: number;
  priceRange?: PriceRange;
};

export type LessonTypeOptionGroup = {
  label: string;
  options: LessonTypeOption[];
  defaultDurationMins?: number;
};

type LessonTypeRates = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        useGetLessonTypeOptions_staffProfile$data["lessonTypes"]
      >["edges"]
    >[number]
  >["node"]
>["rates"];

type PriceRange = { minPrice: number; maxPrice: number };

type LessonChargeRule = {
  duration?: number | null;
  minPlayers?: number | null;
  price: number;
};

export const useGetLessonTypeOptions = (
  instructorRef: useGetLessonTypeOptions_staffProfile$key | null,
  facilityRef: useGetLessonTypeOptions_facility$key | null,
  sessionRef: useGetLessonTypeOptions_session$key | null
) => {
  const session = useFragment(
    useGetLessonTypeOptionsSessionFragmentString,
    sessionRef
  );
  const currentMembershipTypeId =
    session?.currentClientProfile?.member?.membershipType?.id || "";

  const facility = useFragment(
    useGetLessonTypeOptionsFacilityFragmentString,
    facilityRef
  );
  const showLessonPrices = useFacilityShowLessonPrices(facility ?? null);

  const instructor = useFragment(
    useGetLessonTypeOptionsFragmentString,
    instructorRef
  );

  const lessonTypes =
    (instructor?.lessonTypes?.edges || [])
      .filter((e) => !e?.node?.archivedAt)
      ?.flatMap((e) =>
        e?.node &&
        e.node.visibleToMembers &&
        lessonTypeHasRateForMembershipType(e.node, currentMembershipTypeId)
          ? [e.node]
          : []
      ) || [];

  const lessonTypeOptions = lessonTypes.reduce<LessonTypeOptionGroup[]>(
    (acc, lessonType) => {
      // Make sure we sort by ascending duration
      const durations = (
        lessonType.durations?.flatMap((d) => (d ? [d] : [])) || []
      ).sort((a, b) => a - b);

      const options = durations.map((duration) => ({
        duration: duration,
        value: `${lessonType.id}@${duration}`,
        label: (
          <LessonTypeOptionLabel
            duration={duration}
            priceRange={calculatePriceRange(lessonType.rates, duration)}
            showLessonPrices={showLessonPrices}
          />
        ),
        priceRange: calculatePriceRange(lessonType.rates, duration),
        triggerElement: (
          <LessonTypeOptionTrigger
            name={lessonType.name}
            duration={duration}
            priceRange={calculatePriceRange(lessonType.rates, duration)}
            showLessonPrices={showLessonPrices}
          />
        ),
      }));
      acc.push({
        label: lessonType.name,
        options,
        defaultDurationMins: lessonType.defaultDurationMins ?? undefined,
      });
      return acc;
    },
    []
  );

  return lessonTypeOptions;
};

const calculatePriceRange = (rates: LessonTypeRates, duration: number) => {
  if (!rates || !rates.edges || rates?.edges?.length === 0) {
    return { maxPrice: 0, minPrice: 0 };
  }

  const firstRate = rates.edges?.find((edge) =>
    edge?.node?.chargeRuleSets?.edges?.some((e) =>
      e?.node?.chargeRules?.edges?.some((cr) => cr?.node?.duration === duration)
    )
  )?.node;

  const chargeRuleSets =
    firstRate?.chargeRuleSets?.edges?.flatMap((e) =>
      e?.node
        ? [
            e.node.chargeRules?.edges?.flatMap((chargeRuleEdge) =>
              chargeRuleEdge?.node ? [chargeRuleEdge.node] : []
            ) || [],
          ]
        : []
    ) || [];

  return calculateChargeRuleSetsPriceRange(chargeRuleSets, duration);
};

export const calculateChargeRuleSetsPriceRange = (
  chargeRuleSets: ReadonlyArray<ReadonlyArray<LessonChargeRule>>,
  duration: number
): PriceRange => {
  const applicableChargeRuleSets = chargeRuleSets.map((chargeRules) =>
    chargeRules.filter((cr) => cr.duration === duration)
  );

  const playerCounts = Array.from(
    new Set(
      applicableChargeRuleSets.flatMap((chargeRules) =>
        chargeRules.map(getChargeRulePlayerCount)
      )
    )
  );
  const prices = playerCounts.map(
    (playerCount) =>
      sumBy(applicableChargeRuleSets, (chargeRules) => {
        const applicableChargeRule = maxBy(
          chargeRules.filter(
            (chargeRule) => getChargeRulePlayerCount(chargeRule) <= playerCount
          ),
          getChargeRulePlayerCount
        );

        return applicableChargeRule?.price ?? 0;
      }) * playerCount
  );

  const minPrice = minBy(prices) ?? 0;
  const maxPrice = maxBy(prices) ?? 0;

  return { minPrice, maxPrice };
};

const getChargeRulePlayerCount = (chargeRule: LessonChargeRule) =>
  Math.max(chargeRule.minPlayers ?? 1, 1);

const LessonTypeOptionLabel = ({
  duration,
  priceRange,
  showLessonPrices,
}: {
  duration: number;
  priceRange: PriceRange;
  showLessonPrices: boolean;
}) => {
  return (
    <HStack gap={2}>
      <Text variant="mainSmallSemiBold">{t`${duration} minutes`}</Text>
      {showLessonPrices ? <PriceDisplay priceRange={priceRange} /> : null}
    </HStack>
  );
};

const LessonTypeOptionTrigger = ({
  name,
  duration,
  priceRange,
  showLessonPrices,
}: {
  name: string;
  duration: number;
  priceRange: PriceRange;
  showLessonPrices: boolean;
}) => {
  return (
    <HStack gap={2}>
      <Text variant="mainSmallSemiBold">
        {name} ({duration}
        {t`m)`}
      </Text>
      {showLessonPrices ? <PriceDisplay priceRange={priceRange} /> : null}
    </HStack>
  );
};

const useGetLessonTypeOptionsSessionFragmentString = graphql`
  fragment useGetLessonTypeOptions_session on Session {
    currentClientProfile {
      member {
        membershipType {
          id
        }
      }
    }
  }
`;

const useGetLessonTypeOptionsFacilityFragmentString = graphql`
  fragment useGetLessonTypeOptions_facility on Facility {
    ...useFacilityShowLessonPrices_facility
  }
`;

const useGetLessonTypeOptionsFragmentString = graphql`
  fragment useGetLessonTypeOptions_staffProfile on StaffProfile {
    lessonTypes(facilitySlug: $facilitySlug) {
      edges {
        node {
          ...lessonTypeHasRateForMembershipType_lessonType
          id
          archivedAt
          description
          defaultDurationMins
          name
          visibleToMembers
          durations(instructorId: $instructorId)
          rates(
            membershipTypeId: $membershipTypeId
            instructorIds: [$instructorId]
          ) {
            edges {
              node {
                chargeRuleSets {
                  edges {
                    node {
                      chargeRules {
                        edges {
                          node {
                            duration
                            price
                            minPlayers
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
